import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import { WEBHOOK_TRIGGERS } from "models";
import { useDealerChecklistsQuery, useDeleteWebhookMutation, useTriggersQuery } from "modules/WebhooksDashboard/hooks";
import { toISOString } from "utils/dateHelperFunctions";

import type { Checklist } from "models";
import type { CreateWebhookPayload } from "modules/WebhooksDashboard/components/CrudWebhookModal/hooks";

type CreateWebhookFormProps = {
  hasErrors: boolean;
  isPending: boolean;
  webhookId: number | null;
  locationChecklists: { id: number; checklists: Checklist[] }[];
  onClose: () => void;
  onSubmit: (values: CreateWebhookPayload) => void;
};

const FormHelper = createFormHelper<CreateWebhookPayload>();

export const CrudWebhookForm = ({ hasErrors, isPending, onClose, webhookId, onSubmit, locationChecklists }: CreateWebhookFormProps) => {
  const { t } = useTranslation();

  const [showDeleteUser, setShowDeleteUser] = useState(false);

  const { data: triggers } = useTriggersQuery();
  const { selectedDealer } = useSelectedDealer();
  const { data: dealer } = useDealerChecklistsQuery();
  const { mutateAsync: deleteWebhook, isPending: isDeletePending } = useDeleteWebhookMutation();

  const { watch, handleSubmit } = FormHelper.useFormContext();
  const { fields: headerFields, append, remove } = FormHelper.useFieldArray({ name: "headers" });
  const [name, webhookTriggerId, url, dealerLocationId, headers, blockCreatingSupportTicket] = watch([
    "name",
    "webhook_trigger_id",
    "url",
    "dealer_location_id",
    "headers",
    "block_creating_support_ticket"
  ]);

  const isCreate = webhookId === -1;
  const isAppendHeadersDisabled = Array.isArray(headers) && headers.some(el => !el.name || !el.value);
  const dealerLocationsOptions = selectedDealer?.locations?.map(({ id, name }) => ({ key: id, value: id, text: name })) ?? [];
  const dealerChecklistOptions = dealer?.checklists?.map(({ id, name }) => ({ key: id, value: id, text: name })) ?? [];
  const selectedLocationChecklists = locationChecklists.find(el => el.id === dealerLocationId)?.checklists || [];
  const locationChecklistOptions = selectedLocationChecklists.map(({ id, name }) => ({ key: id, value: id, text: name }));

  const triggersOptions =
    triggers?.map(({ id, name, description }) => ({
      key: id,
      value: id,
      text: t(`v8_${name}`) || name,
      content: (
        <div className="triggers-container">
          <span> {t(`v8_${name}`) || name}</span>
          <span className="triggers-description-text-color">{t(`v8_${description}`) || description}</span>
        </div>
      )
    })) ?? [];

  const handleAppendHeaders = () => append({ name: "", value: "", id: 0, created_on: toISOString(new Date()), updated_on: toISOString(new Date()), webhook_id: 0 });
  const handleOpenDeleteModal = () => setShowDeleteUser(true);
  const handleCloseDeleteModal = () => setShowDeleteUser(false);
  const handleDeleteWebhook = async () => {
    if (!webhookId) return;

    await deleteWebhook(webhookId);
    onClose();
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <div className="header-title">{isCreate ? t("v8_create_webhook", "Create Webhook") : t("v8_edit_webhook", "Edit Webhook")}</div>
          <div className="header-checkbox-container">
            <FormHelper.Field.Checkbox name="queued" label={t("v8_queued", "Queued")} />
          </div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!name && hasErrors}>
              <label>{t("v8_name", "Name")} </label>
              <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_name", "Name"), fluid: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field>
              <label>{t("v8_default_location", "Default Location")} </label>
              <FormHelper.Field.Dropdown
                name="dealer_location_id"
                options={dealerLocationsOptions}
                dropdownProps={{
                  placeholder: t("v8_select_default_location", "Select Default Location"),
                  selection: true,
                  clearable: true,
                  selectOnBlur: false
                }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!name && hasErrors}>
              <label>{t("v8_trigger", "Trigger")} </label>
              <FormHelper.Field.Dropdown
                name="webhook_trigger_id"
                options={triggersOptions}
                dropdownProps={{
                  placeholder: t("v8_select_trigger", "Select Trigger"),
                  selection: true,
                  clearable: true,
                  selectOnBlur: false
                }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>

        {webhookTriggerId === WEBHOOK_TRIGGERS.CheckCreated && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field>
                <label>{t("v8_checklist", "Checklist")}</label>
                <FormHelper.Field.Dropdown
                  name="checklist_id"
                  options={dealerLocationId ? locationChecklistOptions : dealerChecklistOptions}
                  dropdownProps={{
                    placeholder: t("v8_select_checklist", "Select Checklist"),
                    selection: true,
                    clearable: true,
                    selectOnBlur: false
                  }}
                />
              </Form.Field>
            </GridColumn>
          </Grid>
        )}

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!url && hasErrors}>
              <label>{t("v8_url", "URL")}</label>
              <FormHelper.Field.Input name="url" inputProps={{ placeholder: t("v8_url", "URL"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn width={16}>
            <Form.Field>
              <label>{t("v8_headers", "Headers")} </label>
            </Form.Field>

            {headerFields.map((header, index) => (
              <Grid columns="equal" key={`webhook-crud-headers-${header.id}`}>
                <GridColumn width={8}>
                  <Form.Field>
                    <FormHelper.Field.ArrayInput fieldArrayName="headers" index={index} fieldKey="name" inputProps={{ placeholder: t("v8_name", "Name"), fluid: true }} />
                  </Form.Field>
                </GridColumn>
                <GridColumn width={7}>
                  <Form.Field>
                    <FormHelper.Field.ArrayInput
                      fieldArrayName="headers"
                      index={index}
                      fieldKey="value"
                      inputProps={{ placeholder: t("v8_value", "Value"), fluid: true }}
                    />
                  </Form.Field>
                </GridColumn>

                <GridColumn>
                  <div className="headers-delete-button-container">
                    <Form.Button icon basic type="button" onClick={() => remove(index)}>
                      <Icon className="trash red" />
                    </Form.Button>
                  </div>
                </GridColumn>
              </Grid>
            ))}
          </GridColumn>
        </Grid>

        <Grid>
          <GridColumn>
            <Form.Button icon labelPosition="left" className="add-headers-button" type="button" onClick={handleAppendHeaders} disabled={isAppendHeadersDisabled}>
              <Icon className="plus add-headers-button-icon" />
              <span>{t("v8_add_headers", "Add Headers")}</span>
            </Form.Button>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn width={3}>
            <FormHelper.Field.Checkbox name="active" label={t("v8_enable_webhook", "Enable Webhook")} />
          </GridColumn>

          <GridColumn width={3}>
            <FormHelper.Field.Checkbox
              name="block_creating_support_ticket"
              label={t("v8_send_support_tickets", "Send Support Tickets")}
              checkboxProps={{ checked: !blockCreatingSupportTicket }}
            />
          </GridColumn>
        </Grid>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          {!isCreate && (
            <Can I="delete" the="webhooks">
              <div className="action-buttons-container">
                <Form.Button color="red" type="button" onClick={handleOpenDeleteModal}>
                  {t("v8_delete", "Delete")}
                  <Icon className="trash" />
                </Form.Button>
              </div>
            </Can>
          )}

          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending} onClick={handleSubmit(d => onSubmit(d))}>
              {isCreate ? t("v8_save", "Save") : t("v8_update", "Update")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDeleteUser}
        handleCancel={handleCloseDeleteModal}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDeleteWebhook}
        isLoading={isDeletePending}
      />
    </>
  );
};
